/**
 * Sandboxed River API
 * 
 * Provides controlled access to River functionality for dynamic apps/tools.
 * All operations are validated, scoped to user, and rate-limited.
 */

import { createFileLogger } from '../utils/logger';
import { entityService } from './entityService';
import useEntityStore from '../stores/entityStore';

const log = createFileLogger('SANDBOXED_API');

interface SandboxContext {
  userId: string;
  spaceId?: string;
  windowId?: string;
  appId?: string;
  toolId?: string;
}

/**
 * Create sandboxed River API for an app or tool
 */
export function createSandboxedAPI(context: SandboxContext) {
  const { userId, spaceId, appId, toolId } = context;
  
  log.info('[createSandboxedAPI] Creating sandbox for:', { userId, spaceId, appId, toolId });
  
  return {
    // Entity operations (scoped to user)
    entity: {
      /**
       * Create an entity (scoped to current user)
       */
      async create(data: any) {
        log.info('[sandbox.entity.create]', { entityType: data.entityType });
        
        // Validate required fields
        if (!data.entityType || !data.name) {
          throw new Error('entityType and name are required');
        }
        
        // Check permissions
        if (appId) {
          const { permissionSystem } = await import('../apps/AppBuilderApp/services/permissionSystem');
          const hasWritePermission = permissionSystem.has(appId, userId, 'write_entities');
          if (!hasWritePermission) {
            throw new Error('Permission denied: App does not have write_entities permission');
          }
        }
        
        // Validate schema and quota — both are hard limits
        const { schemaValidation } = await import('../apps/AppBuilderApp/services/schemaValidation');
        const validation = schemaValidation.validate(data.entityType, data.skeleton || {});
        if (!validation.valid) {
          throw new Error(`Schema validation failed: ${validation.errors.join(', ')}`);
        }
        const quota = await schemaValidation.checkQuota(data.entityType, userId);
        if (!quota.allowed) {
          throw new Error(`Quota exceeded: Maximum ${quota.limit} entities of type ${data.entityType} reached`);
        }
        
        // Force user scoping
        const entityData = {
          ...data,
          spaceId: spaceId || data.spaceId,
          // User ID is set server-side, can't be overridden
        };
        
        try {
          const entity = await entityService.create(entityData);
          log.info('[sandbox.entity.create] Created:', entity._id);
          return entity;
        } catch (error: any) {
          log.error('[sandbox.entity.create] Failed:', error);
          throw new Error(`Failed to create entity: ${error.message}`);
        }
      },
      
      /**
       * Get entity by ID (with permission check)
       *
       * The bridge sends { id } as a single params object (LivePreview routes all
       * entity calls as entityAPI[method](params)), so we accept both forms.
       */
      async get(paramsOrId: { id: string } | string) {
        const entityId = typeof paramsOrId === 'string' ? paramsOrId : paramsOrId.id;
        log.info('[sandbox.entity.get]', { entityId });
        
        try {
          const entity = await entityService.get(entityId);
          
          // Check user has access
          // Allow if: user owns it, has ACL access, or it's in the same space/org
          const entityData = entity as any;
          const hasUserAccess = entityData.user_id === userId;
          const hasACLAccess = entityData.acl?.some((a: any) => a.subject === userId);
          const inSameSpace = entityData.spaceId === spaceId;
          
          // For org-scoped entities (like Products), check if in same dataspace
          // The backend already filtered by the current org/space context
          const isOrgEntity = !!entityData.orgId;
          
          if (!hasUserAccess && !hasACLAccess && !inSameSpace && !isOrgEntity) {
            log.warn('[sandbox.entity.get] Access check failed:', {
              hasUserAccess,
              hasACLAccess,
              inSameSpace,
              isOrgEntity,
              entityUserId: entityData.user_id,
              sandboxUserId: userId,
              entitySpaceId: entityData.spaceId,
              sandboxSpaceId: spaceId,
              entityOrgId: entityData.orgId
            });
            throw new Error('Access denied: You do not have access to this entity');
          }
          
          return entity;
        } catch (error: any) {
          log.error('[sandbox.entity.get] Failed:', error);
          throw new Error(`Failed to get entity: ${error.message}`);
        }
      },
      
      /**
       * Query entities (automatically scoped to user)
       */
      async query(filters: any) {
        log.info('[sandbox.entity.query]', { filters });
        
        try {
          // ✅ FIX: Use entityStore cache instead of making new HTTP request
          // entityStore already has all user entities loaded, properly filtered by org
          // Making a new HTTP request bypasses the cache and can have different filtering
          
          let entities = useEntityStore.getState().entities;

          // Never expose internal system entity types to sandboxed apps.
          entities = entities.filter(e => e.entityType !== 'RiverAppStorage');
          
          // Apply filters client-side
          if (filters.entityType) {
            entities = entities.filter(e => e.entityType === filters.entityType);
          }
          
          if (filters.searchQuery) {
            const query = filters.searchQuery.toLowerCase();
            entities = entities.filter(e => 
              e.name?.toLowerCase().includes(query) ||
              e.llmId?.toLowerCase().includes(query)
            );
          }
          
          if (filters.filters) {
            entities = entities.filter(e => {
              return Object.entries(filters.filters).every(([key, value]) => {
                const keys = key.split('.');
                let current: any = e;
                for (const k of keys) {
                  if (!current || typeof current !== 'object') return false;
                  current = current[k];
                }
                
                // Handle MongoDB operators
                if (typeof value === 'object' && value !== null) {
                  const v = value as Record<string, unknown>;
                  if ('$lt' in v) return current < (v.$lt as number);
                  if ('$gt' in v) return current > (v.$gt as number);
                  if ('$gte' in v) return current >= (v.$gte as number);
                  if ('$lte' in v) return current <= (v.$lte as number);
                  if ('$ne' in v) return current !== v.$ne;
                  if ('$in' in v) return (v.$in as unknown[]).includes(current);
                }
                
                return current === value;
              });
            });
          }
          
          // Apply limit
          if (filters.limit) {
            entities = entities.slice(0, filters.limit);
          }
          
          log.info('[sandbox.entity.query] Found:', entities.length);

          // Strip large binary blobs before sending to the iframe — apps should use
          // readContent() to fetch entity content, not the query result.
          const safeEntities = entities.map(e => {
            const skeleton = (e as any).skeleton;
            if (!skeleton?.base64) return e;
            const { base64: _stripped, ...skeletonWithoutBinary } = skeleton;
            return { ...e, skeleton: skeletonWithoutBinary };
          });
          
          if (safeEntities.length === 0 && filters.entityType === 'BankTransaction') {
            log.warn('[sandbox.entity.query] ⚠️ No BankTransaction entities found in entityStore!');
            
            // Debug info
            const allEntities = useEntityStore.getState().entities;
            log.warn('[sandbox.entity.query] EntityStore has:', {
              total: allEntities.length,
              entityTypes: [...new Set(allEntities.map(e => e.entityType))].slice(0, 20)
            });
          }
          
          if (safeEntities.length > 0) {
            log.info('[sandbox.entity.query] Sample entity:', {
              id: safeEntities[0]._id,
              name: safeEntities[0].name,
              entityType: safeEntities[0].entityType,
              hasOrgId: !!(safeEntities[0] as any).org_id,
              orgId: (safeEntities[0] as any).org_id
            });
          }
          
          return safeEntities;
        } catch (error: any) {
          log.error('[sandbox.entity.query] Failed:', error);
          throw new Error(`Failed to query entities: ${error.message}`);
        }
      },
      
      /**
       * Update entity (with ownership check)
       *
       * Bridge sends { id, updates } as a single params object.
       */
      async update(paramsOrId: { id: string; updates: any } | string, updatesArg?: any) {
        const entityId = typeof paramsOrId === 'string' ? paramsOrId : paramsOrId.id;
        const updates = typeof paramsOrId === 'string' ? updatesArg : paramsOrId.updates;
        log.info('[sandbox.entity.update]', { entityId });
        
        try {
          // Get entity first to check ownership
          const entity = await entityService.get(entityId);
          const entityData = entity as any;
          
          if (userId && entityData.user_id && entityData.user_id !== userId) {
            throw new Error('Access denied: You do not own this entity');
          }
          
          const updated = await entityService.update(entityId, updates);
          log.info('[sandbox.entity.update] Updated:', entityId);
          return updated;
        } catch (error: any) {
          log.error('[sandbox.entity.update] Failed:', error);
          throw new Error(`Failed to update entity: ${error.message}`);
        }
      },
      
      /**
       * Delete entity (with ownership check)
       *
       * Bridge sends { id } as a single params object.
       */
      async delete(paramsOrId: { id: string } | string) {
        const entityId = typeof paramsOrId === 'string' ? paramsOrId : paramsOrId.id;
        log.info('[sandbox.entity.delete]', { entityId });
        
        try {
          const entity = await entityService.get(entityId);
          const entityData = entity as any;
          
          if (entityData.user_id !== userId) {
            throw new Error('Access denied: You do not own this entity');
          }
          
          await entityService.deleteEntity(entityId);
          log.info('[sandbox.entity.delete] Deleted:', entityId);
          return { success: true };
        } catch (error: any) {
          log.error('[sandbox.entity.delete] Failed:', error);
          throw new Error(`Failed to delete entity: ${error.message}`);
        }
      },

      /**
       * Read the content of any workspace entity.
       * The entity does NOT need to be open in a window.
       *
       * Return shape varies by entity type:
       *   Doc / Note / PDF / Presentation → { markdown, text, wordCount, entityType, name }
       *   Sheet                           → { csv, rows, headers, rowCount, colCount, entityType, name }
       *   Canvas                          → { text, elements, entityType, name }
       *   Image (File.Image.*)            → { url, thumbnailUrl, width, height, mimeType, entityType, name }
       *   File.* (misc/code/data)         → { text, mimeType, encoding, entityType, name }
       */
      async readContent(params: { entityId: string }) {
        const { entityId } = params;
        log.info('[sandbox.entity.readContent]', { entityId });

        try {
          const entity = await entityService.get(entityId);
          const entityData = entity as any;

          // Access check
          const hasUserAccess = entityData.user_id === userId;
          const hasACLAccess = entityData.acl?.some((a: any) => a.subject === userId);
          const inSameSpace = entityData.spaceId === spaceId;
          const isOrgEntity = !!entityData.orgId;

          if (!hasUserAccess && !hasACLAccess && !inSameSpace && !isOrgEntity) {
            throw new Error('Access denied: You do not have access to this entity');
          }

          const type: string = entityData.entityType || '';
          const skeleton = entityData.skeleton || {};
          const base64: string = skeleton.base64 || '';

          // ── Image entities ─────────────────────────────────────────────
          const IMAGE_FORMATS = ['jpeg', 'jpg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff', 'ico', 'avif'];
          if (
            type.toLowerCase().includes('image') ||
            type.startsWith('File.Image') ||
            IMAGE_FORMATS.includes(type.toLowerCase())
          ) {
            const { FILES_ENDPOINTS } = await import('../endpoints');
            const resolvedId: string = entityData._id || entityId;
            const url = FILES_ENDPOINTS.read(resolvedId, undefined, 'full');
            const thumbnailUrl = FILES_ENDPOINTS.read(resolvedId, undefined, 'thumbnail');

            log.info('[sandbox.entity.readContent] Image entity:', entityId);
            return {
              url,
              thumbnailUrl,
              width: skeleton.width ?? null,
              height: skeleton.height ?? null,
              mimeType: skeleton.mimeType || skeleton.format || null,
              entityType: type,
              name: entityData.name,
            };
          }

          // ── Generic File entities (code, data, misc) ───────────────────
          if (type.startsWith('File.') && !type.startsWith('File.Image')) {
            const { FileContentService } = await import('../apps/FileViewerApp/services/fileContentService');
            const fileContent = await FileContentService.fetchFileContent(entityId);

            log.info('[sandbox.entity.readContent] File entity:', { entityId, mimeType: fileContent.mimeType });
            return {
              text: fileContent.content,
              mimeType: fileContent.mimeType,
              encoding: fileContent.encoding || 'utf-8',
              entityType: type,
              name: entityData.name,
            };
          }

          // ── Canvas entities (v2 — Y.js binary) ────────────────────────
          if (type === 'Canvas') {
            const { decodeYjsCanvas } = await import('../utils/yjsContentDecoder');
            const decoded = base64
              ? decodeYjsCanvas(base64)
              : { elements: [], text: '', elementCount: 0 };

            log.info('[sandbox.entity.readContent] Canvas entity:', { entityId, elementCount: decoded.elementCount });
            return {
              text: decoded.text,
              elements: decoded.elements,
              elementCount: decoded.elementCount,
              entityType: type,
              name: entityData.name,
            };
          }

          // ── Sheet entities ─────────────────────────────────────────────
          if (type === 'Sheet') {
            if (!base64) {
              log.info('[sandbox.entity.readContent] Sheet has no base64 content:', entityId);
              return { csv: '', rows: [], headers: [], rowCount: 0, colCount: 0, entityType: type, name: entityData.name };
            }

            const { decodeYjsSheet } = await import('../utils/yjsContentDecoder');
            const decoded = decodeYjsSheet(base64);

            log.info('[sandbox.entity.readContent] Sheet decoded:', { entityId, rowCount: decoded.rowCount, colCount: decoded.colCount });
            return {
              csv: decoded.csv,
              rows: decoded.rows,
              headers: decoded.headers,
              rowCount: decoded.rowCount,
              colCount: decoded.colCount,
              entityType: type,
              name: entityData.name,
            };
          }

          // ── Doc / Note / PDF / Presentation (ProseMirror Y.js) ────────
          if (!base64) {
            log.info('[sandbox.entity.readContent] No base64 content for:', entityId);
            return { markdown: '', text: '', wordCount: 0, entityType: type, name: entityData.name };
          }

          const { decodeYjsContent } = await import('../utils/yjsContentDecoder');
          const decoded = decodeYjsContent(base64);

          log.info('[sandbox.entity.readContent] Decoded:', {
            entityId,
            entityType: type,
            wordCount: decoded.wordCount,
            markdownLength: decoded.markdown.length,
          });

          return {
            markdown: decoded.markdown,
            text: decoded.text,
            wordCount: decoded.wordCount,
            entityType: type,
            name: entityData.name,
          };
        } catch (error: any) {
          log.error('[sandbox.entity.readContent] Failed:', error);
          throw new Error(`Failed to read content: ${error.message}`);
        }
      }
    },
    
    // Utility functions
    utils: {
      /**
       * Format date
       */
      formatDate(date: Date | string): string {
        const d = typeof date === 'string' ? new Date(date) : date;
        return d.toLocaleDateString();
      },
      
      /**
       * Generate unique ID
       */
      generateId(): string {
        return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
    },
    
    // Context discovery and data access
    context: {
      /**
       * Discover available data sources and suggest integrations
       * Used before building apps to check what context exists
       */
      async discover(options: {
        intent?: string;
        entityTypes?: string[];
        includeSchema?: boolean;
      }) {
        log.info('[sandbox.context.discover]', { intent: options.intent });
        
        try {
          const { useCommandRegistryStore } = await import('../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          
          const result = await executeCommand('context_discover', options) as any;
          
          if (result.status !== 'success') {
            throw new Error(result.message || 'Context discovery failed');
          }
          
          return result.data;
        } catch (error: any) {
          log.error('[sandbox.context.discover] Failed:', error);
          throw new Error(`Failed to discover context: ${error.message}`);
        }
      },
      
      /**
       * List all integrations (connected and available)
       */
      async listIntegrations() {
        log.info('[sandbox.context.listIntegrations]');
        
        try {
          const { useCommandRegistryStore } = await import('../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          
          const result = await executeCommand('integration_list', {}) as any;
          
          if (result.status !== 'success') {
            throw new Error(result.message || 'Failed to list integrations');
          }
          
          return result.data.integrations;
        } catch (error: any) {
          log.error('[sandbox.context.listIntegrations] Failed:', error);
          throw new Error(`Failed to list integrations: ${error.message}`);
        }
      },
      
      /**
       * Get context info (user, space, app)
       */
      info: {
        userId,
        spaceId,
        windowId: context.windowId,
        appId: context.appId,
        toolId: context.toolId
      }
    },
    
    // Smart data access layer
    data: {
      /**
       * High-level data query with automatic integration checking
       * This checks if required integration is connected before querying
       */
      async query(options: {
        dataType: 'bank_transactions' | 'emails' | 'calendar_events' | 'files';
        filters?: Record<string, any>;
        searchQuery?: string;
        limit?: number;
      }) {
        log.info('[sandbox.data.query]', { dataType: options.dataType });
        
        try {
          const { useCommandRegistryStore } = await import('../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          
          const result = await executeCommand('data_query', options) as any;
          
          if (result.status !== 'success') {
            // If integration missing, return helpful object
            if (result.error?.details?.requiredIntegration) {
              return {
                needsSetup: true,
                integration: result.error.details.requiredIntegration,
                integrationName: result.error.details.integrationName,
                message: result.message
              };
            }
            throw new Error(result.message || 'Data query failed');
          }
          
          return {
            data: result.data.entities,
            count: result.data.count,
            dataType: result.data.dataType,
            integration: result.data.integration
          };
        } catch (error: any) {
          log.error('[sandbox.data.query] Failed:', error);
          throw new Error(`Failed to query data: ${error.message}`);
        }
      },
      
      /**
       * React hook version for use in app components
       * Note: This returns a promise, actual React hook would be added in a separate file
       */
      async use(options: {
        dataType: 'bank_transactions' | 'emails' | 'calendar_events' | 'files';
        filters?: Record<string, any>;
      }) {
        // This would be implemented as a proper React hook in the app
        // For now, return the query result
        return this.query(options);
      }
    },
    
    // Integration management
    integrations: {
      /**
       * List connected integrations
       */
      async list() {
        log.info('[sandbox.integrations.list]');
        
        try {
          const { useCommandRegistryStore } = await import('../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          
          const result = await executeCommand('integration_list', {}) as any;
          
          if (result.status !== 'success') {
            throw new Error(result.message || 'Failed to list integrations');
          }
          
          return result.data.integrations;
        } catch (error: any) {
          log.error('[sandbox.integrations.list] Failed:', error);
          throw new Error(`Failed to list integrations: ${error.message}`);
        }
      },
      
      /**
       * Check if specific integration is connected
       */
      async isConnected(type: string): Promise<boolean> {
        const integrations = await this.list();
        return integrations.some((i: any) => i.type === type && i.status === 'connected');
      },
      
      /**
       * Trigger integration setup (opens modal in UI)
       */
      async setup(type: string) {
        log.info('[sandbox.integrations.setup]', { type });
        
        // Post message to parent window to open integration setup
        if (typeof window !== 'undefined') {
          window.parent.postMessage({
            type: 'RIVER_INTEGRATION_SETUP',
            integration: type
          }, '*');
        }
      }
    },
    
    // Tool execution — calls any registered River command with the current user's context.
    // Always returns StandardToolResult ({ status, message, data, ... }) — never throws.
    // Check result.status === 'success' before using result.data.
    tools: {
      async execute(params: { toolName: string; params: any }) {
        log.info('[sandbox.tools.execute]', { toolName: params.toolName });

        try {
          const { useCommandRegistryStore } = await import('../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();

          const result = await executeCommand(params.toolName, params.params, {
            spaceId: context.spaceId,
            userId,
            windowId: context.windowId,
          }) as any;

          if (result == null) {
            return {
              status: 'error',
              message: `Unknown tool: "${params.toolName}". Make sure the tool name is correct.`,
              format: 'json',
              error: { code: 'unknown_tool', category: 'validation' },
            };
          }

          return result;
        } catch (error: any) {
          log.error('[sandbox.tools.execute] Failed:', error);
          return {
            status: 'error',
            message: `Tool execution failed: ${error.message}`,
            format: 'json',
            error: { code: 'execution_error', category: 'execution', details: error.message },
          };
        }
      }
    },

    storage: {
      /**
       * Persist a value under a given key for this app.
       * Values survive page reloads — they are stored in the backend DB.
       */
      async set(params: { key: string; value: any }) {
        const { key, value } = params;
        if (!key || typeof key !== 'string') throw new Error('storage.set: key must be a non-empty string');
        await _withStorageWriteLock(appId, async () => {
          const entity = await _getOrCreateStorageEntity(appId, spaceId, userId);
          const data = { ...(entity.skeleton?.data ?? {}), [key]: value };
          await entityService.update(entity._id, { skeleton: { ...entity.skeleton, data } });
        });
        log.info('[sandbox.storage.set]', { appId, key });
      },

      /**
       * Read a previously persisted value by key. Returns undefined if not set.
       */
      async get(params: { key: string }) {
        const { key } = params;
        if (!key || typeof key !== 'string') throw new Error('storage.get: key must be a non-empty string');
        const entity = await _getOrCreateStorageEntity(appId, spaceId, userId);
        return (entity.skeleton?.data ?? {})[key] ?? null;
      },

      /**
       * Return all persisted key-value pairs for this app as a plain object.
       */
      async getAll() {
        const entity = await _getOrCreateStorageEntity(appId, spaceId, userId);
        return entity.skeleton?.data ?? {};
      },

      /**
       * Remove a key from persistent storage.
       */
      async delete(params: { key: string }) {
        const { key } = params;
        if (!key || typeof key !== 'string') throw new Error('storage.delete: key must be a non-empty string');
        await _withStorageWriteLock(appId, async () => {
          const entity = await _getOrCreateStorageEntity(appId, spaceId, userId);
          const data = { ...(entity.skeleton?.data ?? {}) };
          delete data[key];
          await entityService.update(entity._id, { skeleton: { ...entity.skeleton, data } });
        });
        log.info('[sandbox.storage.delete]', { appId, key });
      },
    },

    // Context information (keep for backward compatibility)
    get contextInfo() {
      return {
        userId,
        spaceId,
        windowId: context.windowId,
        appId: context.appId,
        toolId: context.toolId
      };
    }
  };
}

/**
 * Deduplication map for in-flight entity creation requests.
 * Prevents duplicate RiverAppStorage entities when multiple storage calls
 * race on first use (e.g. several storage.set calls in the same useEffect).
 */
const _storageCreationInFlight = new Map<string, Promise<any>>();

/**
 * Per-app write lock: ensures concurrent set/delete calls are serialised so
 * read-modify-write cycles never clobber each other.
 */
const _storageWriteLock = new Map<string, Promise<void>>();

/**
 * Get or create the single RiverAppStorage entity for a given app.
 * All storage operations are funneled through this helper so the entity
 * is lazily created on first use and reused on every subsequent call.
 */
async function _getOrCreateStorageEntity(
  appId: string | undefined,
  spaceId: string | undefined,
  userId: string
) {
  const storageName = `__storage__${appId || 'unknown'}`;

  // Search the entity store cache first (avoids an extra HTTP round-trip).
  const cached = useEntityStore
    .getState()
    .entities.find(
      (e: any) => e.entityType === 'RiverAppStorage' && e.name === storageName
    );

  if (cached) return cached;

  // If there's already an in-flight creation for this app, wait for it
  // instead of issuing a second create — this prevents duplicate entities
  // when several storage calls race on first use.
  const inflight = _storageCreationInFlight.get(storageName);
  if (inflight) return inflight;

  const creation = (async () => {
    // Fall back to a direct backend query (covers cases where the store
    // hasn't loaded this entity type yet).
    const results = await entityService.queryEntities({
      entityType: 'RiverAppStorage',
      name: storageName,
    });

    if (results && results.length > 0) {
      return results[0];
    }

    // None found — create it.
    return entityService.create({
      entityType: 'RiverAppStorage',
      name: storageName,
      spaceId,
      skeleton: { data: {}, appId },
    } as any);
  })();

  _storageCreationInFlight.set(storageName, creation);
  try {
    return await creation;
  } finally {
    _storageCreationInFlight.delete(storageName);
  }
}

/**
 * Run a storage write operation exclusively — queues behind any in-progress
 * write for the same app so concurrent set/delete calls never lose data.
 */
async function _withStorageWriteLock<T>(
  appId: string | undefined,
  fn: () => Promise<T>
): Promise<T> {
  const key = appId || 'unknown';
  const prev = _storageWriteLock.get(key) ?? Promise.resolve();
  let resolveLock!: () => void;
  const lock = new Promise<void>((r) => { resolveLock = r; });
  _storageWriteLock.set(key, lock);
  try {
    await prev;
    return await fn();
  } finally {
    resolveLock();
    // Clean up if no other waiter is pending.
    if (_storageWriteLock.get(key) === lock) {
      _storageWriteLock.delete(key);
    }
  }
}

/**
 * Rate limiter for sandboxed operations
 */
class RateLimiter {
  private operations = new Map<string, number[]>();
  
  check(key: string, maxPerMinute: number): boolean {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    // Get recent operations
    let timestamps = this.operations.get(key) || [];
    
    // Remove old timestamps
    timestamps = timestamps.filter(t => t > oneMinuteAgo);
    
    // Check limit
    if (timestamps.length >= maxPerMinute) {
      return false;
    }
    
    // Add current operation
    timestamps.push(now);
    this.operations.set(key, timestamps);
    
    return true;
  }
}

export const rateLimiter = new RateLimiter();

/**
 * Resource usage tracker
 */
export class ResourceTracker {
  private usage = new Map<string, {
    entityCreations: number;
    entityQueries: number;
    executionTime: number;
  }>();
  
  track(userId: string, operation: string, value: number = 1) {
    const current = this.usage.get(userId) || {
      entityCreations: 0,
      entityQueries: 0,
      executionTime: 0
    };
    
    if (operation === 'entity.create') {
      current.entityCreations += value;
    } else if (operation === 'entity.query') {
      current.entityQueries += value;
    } else if (operation === 'execution') {
      current.executionTime += value;
    }
    
    this.usage.set(userId, current);
  }
  
  getUsage(userId: string) {
    return this.usage.get(userId) || {
      entityCreations: 0,
      entityQueries: 0,
      executionTime: 0
    };
  }
  
  reset(userId: string) {
    this.usage.delete(userId);
  }
}

export const resourceTracker = new ResourceTracker();
